import React, { useState, useEffect } from 'react';
import { Plus, CalendarCheck, Check, X } from 'lucide-react';
import { Card, Button, Input, Select, Table, Modal, Badge } from '../components/ui';
import { areasService, reservasService } from '../services/api';
import { formatDate } from '../utils/formatters';
import toast from 'react-hot-toast';

const ReservasPage = () => {
  const [areas, setAreas] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    area_id: '', fecha: '', hora_inicio: '', hora_fin: '', motivo: '', asistentes: 1
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [areasRes, reservasRes] = await Promise.all([
        areasService.getAll(),
        reservasService.getAll()
      ]);
      setAreas(areasRes.data.data || []);
      setReservas(reservasRes.data.data || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await reservasService.create(form);
      toast.success('Reserva creada');
      setModalOpen(false);
      setForm({ area_id: '', fecha: '', hora_inicio: '', hora_fin: '', motivo: '', asistentes: 1 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al crear reserva');
    }
  };

  const handleAprobar = async (id) => {
    try {
      await reservasService.aprobar(id);
      toast.success('Reserva aprobada');
      loadData();
    } catch (error) {
      toast.error('Error al aprobar');
    }
  };

  const handleCancelar = async (id) => {
    try {
      await reservasService.cancelar(id);
      toast.success('Reserva cancelada');
      loadData();
    } catch (error) {
      toast.error('Error al cancelar');
    }
  };

  const statusVariant = { PENDIENTE: 'warning', CONFIRMADA: 'success', CANCELADA: 'danger', COMPLETADA: 'info' };

  const columns = [
    { header: 'Área', render: (row) => row.area_nombre },
    { header: 'Fecha', render: (row) => formatDate(row.fecha) },
    { header: 'Horario', render: (row) => `${row.hora_inicio} - ${row.hora_fin}` },
    { header: 'Dpto', render: (row) => row.departamento_numero || '-' },
    { header: 'Estado', render: (row) => <Badge variant={statusVariant[row.estado]}>{row.estado}</Badge> },
    { header: 'Acciones', render: (row) => row.estado === 'PENDIENTE' && (
      <div className="flex gap-2">
        <button onClick={() => handleAprobar(row.id)} className="p-1.5 rounded-lg hover:bg-emerald-500/20 text-emerald-400"><Check size={16} /></button>
        <button onClick={() => handleCancelar(row.id)} className="p-1.5 rounded-lg hover:bg-rose-500/20 text-rose-400"><X size={16} /></button>
      </div>
    )}
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Áreas Comunes</h1>
          <p className="text-slate-400">Reservas de espacios compartidos</p>
        </div>
        <Button icon={Plus} onClick={() => setModalOpen(true)}>Nueva Reserva</Button>
      </div>

      {/* Áreas disponibles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {areas.map((area) => (
          <Card key={area.id} className="p-4">
            <div className="flex items-start gap-3">
              <div className="text-3xl">{area.imagen || '🏢'}</div>
              <div className="flex-1">
                <h3 className="text-white font-medium">{area.nombre}</h3>
                <p className="text-slate-400 text-sm">{area.descripcion}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                  <span>Cap: {area.capacidad}</span>
                  <span>{area.horario_inicio} - {area.horario_fin}</span>
                  {area.precio > 0 && <span className="text-emerald-400">S/ {area.precio}</span>}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Reservas */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-slate-700">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <CalendarCheck size={20} />
            Reservas
          </h3>
        </div>
        <Table columns={columns} data={reservas} loading={loading} />
      </Card>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Nueva Reserva">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Área"
            value={form.area_id}
            onChange={(e) => setForm({ ...form, area_id: e.target.value })}
            options={areas.map(a => ({ value: a.id, label: `${a.imagen || ''} ${a.nombre}` }))}
            required
          />
          <Input label="Fecha" type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Hora Inicio" type="time" value={form.hora_inicio} onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })} required />
            <Input label="Hora Fin" type="time" value={form.hora_fin} onChange={(e) => setForm({ ...form, hora_fin: e.target.value })} required />
          </div>
          <Input label="Motivo" value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} />
          <Input label="Asistentes" type="number" min="1" value={form.asistentes} onChange={(e) => setForm({ ...form, asistentes: e.target.value })} />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">Reservar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ReservasPage;
